import { useState, useEffect, useRef } from 'react';
import { ClipboardList, ChevronDown } from 'lucide-react';
import { useIssueTemplates, issueTemplateApi } from '../../hooks/useIssueTemplates';
import toast from 'react-hot-toast';

interface IssueTemplateSelectorProps {
  projectId: string;
  onSelectTemplate: (content: string, templateName: string) => void;
}

export default function IssueTemplateSelector({ projectId, onSelectTemplate }: IssueTemplateSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { data, isLoading: isListLoading } = useIssueTemplates(projectId);

  // 외부 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = async (templateId: string, templateName: string) => {
    setIsLoading(true);
    try {
      const template = await issueTemplateApi.fetchTemplate(projectId, templateId);
      onSelectTemplate(template.content, templateName);
      setIsOpen(false);
    } catch {
      toast.error('템플릿을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const categories = data?.categories || [];
  const hasTemplates = categories.some((cat) => cat.templates.length > 0);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
      >
        <ClipboardList size={14} />
        {isLoading ? '적용 중...' : '템플릿 선택'}
        <ChevronDown size={14} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-80 overflow-y-auto">
          {isListLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500">로딩 중...</div>
          ) : !hasTemplates ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              등록된 템플릿이 없습니다. 프로젝트 설정에서 템플릿을 추가해주세요.
            </div>
          ) : (
            categories.map(
              (cat) =>
                cat.templates.length > 0 && (
                  <div key={cat.categoryId}>
                    <div className="px-4 py-1.5 text-sm font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                      {cat.name}
                    </div>
                    {cat.templates.map((tmpl) => (
                      <button
                        key={tmpl.templateId}
                        type="button"
                        onClick={() => handleSelect(tmpl.templateId, tmpl.name)}
                        disabled={isLoading}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-50"
                      >
                        {tmpl.name}
                      </button>
                    ))}
                  </div>
                ),
            )
          )}
        </div>
      )}
    </div>
  );
}
